import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Button, Alert } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useTheme } from '../context/ThemeContext';
import ThemeLogo from './ThemeLogo';

export default function Success() {
  const location = useLocation();
  const { plan, isYearly, isTrial, stripeSubscription } = location.state || {};
  const [updating, setUpdating] = useState(true);
  const [error, setError] = useState(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const planName = plan?.name || 'our plan';
  const billingCycle = isYearly ? 'Yearly' : 'Monthly';
  const assessments = plan?.assessments || 'the standard number of';

  const confirmationMessage = isTrial 
    ? `Congratulations! You've started your 30-day free trial of our ${planName} Plan. Your trial includes ${assessments}. We're excited to have you on board!`
    : `Congratulations! You are now subscribed to our ${planName} Plan (${billingCycle}). Your subscription includes ${assessments}. We're excited to have you on board!`;

  useEffect(() => {
    const updateSubscription = async () => {
      try {
        if (auth.currentUser && plan) {
          console.log('Updating subscription for user:', auth.currentUser.uid);
          console.log('Plan details:', plan, 'Yearly:', isYearly, 'Trial:', isTrial);
          console.log('Stripe subscription:', stripeSubscription);
          
          const userRef = doc(db, 'Users', auth.currentUser.uid);
          const now = new Date();
          
          // Calculate subscription end date based on subscription type and trial status
          let subscriptionEndDate = new Date(now);
          
          if (isTrial && stripeSubscription) {
            // Use Stripe trial end date if available
            if (stripeSubscription.trial_end) {
              subscriptionEndDate = new Date(stripeSubscription.trial_end * 1000);
            } else {
              // Fallback to 30 days for trial
              subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 30);
            }
          } else if (isYearly) {
            subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 365); // 365 days for yearly
          } else {
            subscriptionEndDate.setDate(subscriptionEndDate.getDate() + 30); // 30 days for monthly
          }
          
          // Determine totalAssessments based on plan name
          let totalAssessments = 0;
          switch (plan.name.toLowerCase()) {
            case 'starter':
              totalAssessments = 30;
              break;
            case 'pro':
              totalAssessments = 60;
              break;
            case 'premium':
              totalAssessments = 90;
              break;
            default:
              totalAssessments = 30; // Default to Starter
          }
          
          const subscriptionData = {
            subscriptionName: plan.name,
            subscriptionType: isYearly ? 'Yearly' : 'Monthly',
            subscriptionStartDate: now,
            subscriptionEndDate: subscriptionEndDate,
            totalAssessments: totalAssessments,
            isTrial: isTrial,
            stripeSubscriptionId: stripeSubscription?.id || null,
            lastUpdated: now
          };
          
          await updateDoc(userRef, subscriptionData);
          console.log('Subscription updated successfully');
          setUpdating(false);
        }
      } catch (error) {
        console.error('Error updating subscription:', error);
        setError('Failed to update subscription. Please contact support.');
        setUpdating(false);
      }
    };

    updateSubscription();
  }, [plan, isYearly, isTrial, stripeSubscription]);

  return (
    <div className={`success-page ${isDark ? 'success-page-dark' : 'success-page-light'}`}>
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} className="text-center">
            <div className="success-card">
              {/* Success Icon */}
              <div className="success-icon">
                <div className="success-checkmark">
                  <svg width="60" height="60" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M9 12l2 2 4-4"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              {/* Main Heading */}
              <h1 className="success-heading mb-3">
                {isTrial ? 'Trial Started!' : 'Payment Successful!'}
              </h1>

              {/* Confirmation Message */}
              <p className="success-message mb-4">
                {plan ? confirmationMessage : 'Thank you for subscribing. Your account is now active.'}
              </p>

              {/* Trial Alert */}
              {isTrial && (
                <Alert variant="info" className="trial-alert mb-4">
                  <div className="alert-icon">🎉</div>
                  <div className="alert-content">
                    <strong>Your 30-day free trial has begun!</strong><br />
                    You'll have full access to all features during your trial period. 
                    No charges will be made until the trial ends.
                  </div>
                </Alert>
              )}

              {/* Status Messages */}
              {updating && (
                <div className="status-message updating mb-3">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Updating your subscription...
                </div>
              )}
              
              {error && (
                <div className="status-message error mb-3">
                  <div className="error-icon">⚠️</div>
                  {error}
                </div>
              )}

              {/* Action Button */}
              <Button 
                as={Link} 
                to="/" 
                className="success-button"
                size="lg"
              >
                <i className="bi bi-house me-2"></i>
                Go to Homepage
              </Button>

              {/* Additional Info */}
              <div className="success-info mt-4">
                <p className="info-text">
                  Check your email for confirmation details and next steps.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 